import React, { useState } from 'react';
import {
  Type as TypeIcon,
  ArrowLeft as ArrowLeftIcon,
  Square as SquareIcon,
  Edit2 as PenIcon,
  MousePointer as MouseIcon,
  Circle as FilledCircleIcon,
  Trash2 as TrashIcon,
  Save as SaveIcon,
  X as CloseIcon,
  LucideIcon
} from 'lucide-react';
import { TwitterPicker } from 'react-color';
import cx from 'classnames';
import { Tooltip } from 'antd';
import Draggable from 'react-draggable';
import DraggerIcon from '../../dragger/dragger.svg';
import styles from './style.module.css';
import { Tool } from '../image';

type Props = {
  tool: Tool;
  setTool: (tool: Tool) => void;
  color: string;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  canDelete: boolean;
  onColorChange: (color: string) => void;
  container: HTMLElement | null;
  fixedToWindow?: boolean;
};

const ToolIcon = ({
  Icon,
  className
}: {
  Icon: LucideIcon;
  className?: string;
}) => (
  <>
    {/* @ts-expect-error */}
    <Icon className={cx(styles.icon, { [className]: className })} />
  </>
);

const AnnotateTools = ({
  tool,
  setTool,
  color,
  onCancel,
  onSave,
  onDelete,
  canDelete,
  onColorChange,
  container,
  fixedToWindow = false
}: Props) => {
  const [pickerActive, setPickerActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);

  const handlePicker = () => setPickerActive(!pickerActive);

  const handleColorChange = ({ hex }: { hex: string }) => {
    setPickerActive(false);
    onColorChange(hex);
  };

  const toolClass = (_tool: string) =>
    cx(styles.toolButton, styles[_tool], {
      [styles.toolActive]: tool === _tool
    });

  if (!container) {
    return null;
  }

  const tools = [
    { name: Tool.Mouse, Icon: MouseIcon },
    { name: Tool.Rect, Icon: SquareIcon },
    { name: Tool.Arrow, Icon: ArrowLeftIcon },
    { name: Tool.Draw, Icon: PenIcon },
    { name: Tool.Text, Icon: TypeIcon }
  ];

  return (
    <Draggable
      onStart={() => setIsDragging(true)}
      onStop={() => setIsDragging(false)}
      onDrag={(event, data) => {
        if (hasMoved !== !!(data.x || data.y))
          setHasMoved(!!(data.x || data.y));
      }}
      handle=".dragHandler"
      // @ts-expect-error
      className={styles.draggable}
    >
      <div
        className={cx(styles.tools, {
          [styles.movedTools]: hasMoved
        })}
        style={fixedToWindow ? { position: 'fixed' } : {}}
      >
        <div className={styles.toolsInner}>
          {tools.map(({ name, Icon }) => (
            <button
              key={name}
              onClick={() => setTool(name)}
              className={toolClass(name)}
            >
              <ToolIcon Icon={Icon} />
            </button>
          ))}
          <button
            onClick={() => {
              setTool(Tool.Trash);
              onDelete();
            }}
            className={cx(styles.toolButton, styles.toolButtonTrash, {
              [styles.toolButtonTrashEnabled]: canDelete
            })}
          >
            <ToolIcon Icon={TrashIcon} />
          </button>

          <hr className={styles.break} />
          <span className={styles.pickerContainer}>
            <button
              onClick={handlePicker}
              className={toolClass('paint')}
              style={{ color }}
            >
              <ToolIcon Icon={FilledCircleIcon} className={styles.circleIcon} />
            </button>
            {pickerActive && (
              <TwitterPicker
                triangle="top-right"
                className={styles.picker}
                color={color}
                onChangeComplete={handleColorChange}
              />
            )}
          </span>
          <hr className={styles.break} />
          <Tooltip
            placement="left"
            title="Save annotations"
            mouseEnterDelay={0.5}
            align={{ offset: [-12] }}
            getPopupContainer={() => container}
          >
            <button onClick={() => onSave()} className={styles.save}>
              <ToolIcon Icon={SaveIcon} />
            </button>
          </Tooltip>
          <Tooltip
            placement="left"
            title="Cancel"
            mouseEnterDelay={0.5}
            align={{ offset: [-12] }}
            getPopupContainer={() => container}
          >
            <button onClick={onCancel} className={styles.cancel}>
              <ToolIcon Icon={CloseIcon} />
            </button>
          </Tooltip>
          <Tooltip
            placement="left"
            title="Move me"
            mouseEnterDelay={0.5}
            align={{ offset: [-12] }}
            getPopupContainer={() => container}
          >
            <DraggerIcon
              className={cx(styles.draggerIcon, 'dragHandler', {
                [styles.isDragging]: isDragging
              })}
            />
          </Tooltip>
        </div>
      </div>
    </Draggable>
  );
};

export default AnnotateTools;
